import { forwardRef, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { toast } from 'sonner';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { FIELD_LABEL_CLASS } from '@/components/common/FormDialog/formDialog.constants';
import { NumberMark } from '@/components/common/NumberMark';
import { LabelText } from '@/components/common/LabelText';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SURVEY_ANSWER_TYPE_LABELS,
  SURVEY_CHOICE_QUESTION_TYPES,
  SURVEY_NUMERIC_ANSWER_TYPES,
  SURVEY_QUESTION_TEXT_MAX_LENGTH,
  SURVEY_QUESTION_TYPE_CHANGE_WARNING,
  SURVEY_QUESTION_TYPE_LABELS,
  SURVEY_RATING_DEFAULT_MAX,
  SURVEY_RATING_DEFAULT_MIN,
} from '../../constants/survey.constants';
import {
  SURVEY_ANSWER_TYPE,
  SURVEY_QUESTION_TYPE,
  type CreateSurveyFormValues,
  type SurveyAnswerType,
  type SurveyQuestionType,
} from '../../types/survey.types';
import { QuestionMaxCharsField } from './QuestionMaxCharsField';
import { QuestionOptionsEditor } from './QuestionOptionsEditor';
import { QuestionRangeFields } from './QuestionRangeFields';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FIELD_CONTROL_CLASS, TEXTAREA_CONTROL_CLASS } from '@/styles/fieldControl';
import { DROPDOWN_ITEM_CLASS, DROPDOWN_PANEL_CLASS } from '@/styles/floatingPanel';
import { ReorderButtons } from './primitives/ReorderButtons';

const SELECT_TRIGGER_CLASS = cn(FIELD_CONTROL_CLASS, 'w-full');

interface QuestionBuilderProps {
  questionIndex: number;
  totalQuestions: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
}

const questionTypeItems = SURVEY_QUESTION_TYPE.map((value) => ({
  value,
  label: SURVEY_QUESTION_TYPE_LABELS[value],
}));

const answerTypeItems = SURVEY_ANSWER_TYPE.map((value) => ({
  value,
  label: SURVEY_ANSWER_TYPE_LABELS[value],
}));

function isChoiceType(type: SurveyQuestionType): boolean {
  return SURVEY_CHOICE_QUESTION_TYPES.includes(type);
}

function isNumericAnswer(answerType: SurveyAnswerType | undefined): boolean {
  return answerType !== undefined && SURVEY_NUMERIC_ANSWER_TYPES.includes(answerType);
}

export const QuestionBuilder = forwardRef<HTMLTextAreaElement, QuestionBuilderProps>(
  ({ questionIndex, totalQuestions, onMoveUp, onMoveDown, onRemove }, ref) => {
    const { control, setValue, clearErrors, getValues } = useFormContext<CreateSurveyFormValues>();
    const type = useWatch({ control, name: `questions.${questionIndex}.type` });
    const answerType = useWatch({ control, name: `questions.${questionIndex}.answerType` });
    const questionHeadingId = `question-${questionIndex}-heading`;
    const previousTypeRef = useRef<SurveyQuestionType>(type);
    const previousAnswerTypeRef = useRef<SurveyAnswerType | undefined>(answerType);

    useEffect(() => {
      const previousType = previousTypeRef.current;
      const previousAnswerType = previousAnswerTypeRef.current;
      const typeChanged = previousType !== type;
      const answerTypeChanged = previousAnswerType !== answerType;
      previousTypeRef.current = type;
      previousAnswerTypeRef.current = answerType;

      if (!typeChanged && !answerTypeChanged) return;

      const staleFields: (keyof CreateSurveyFormValues['questions'][number])[] = [];

      if (!isChoiceType(type)) staleFields.push('options');
      if (type !== 'text') {
        staleFields.push('answerType');
        staleFields.push('maxChars');
      }
      const rangeApplies = type === 'rating' || (type === 'text' && isNumericAnswer(answerType));
      if (!rangeApplies) {
        staleFields.push('minValue');
        staleFields.push('maxValue');
      }
      if (type === 'text' && answerType !== 'text') {
        staleFields.push('maxChars');
      }

      const uniqueStale = Array.from(new Set(staleFields));

      if (typeChanged && uniqueStale.length > 0) {
        const persistedId = getValues(`questions.${questionIndex}.id`);
        if (persistedId !== undefined) {
          toast.info(SURVEY_QUESTION_TYPE_CHANGE_WARNING);
        }
      }

      uniqueStale.forEach((fieldName) => {
        setValue(`questions.${questionIndex}.${fieldName}`, undefined, {
          shouldDirty: true,
        });
        clearErrors(`questions.${questionIndex}.${fieldName}`);
      });

      if (typeChanged && type === 'rating') {
        const currentMin = getValues(`questions.${questionIndex}.minValue`);
        const currentMax = getValues(`questions.${questionIndex}.maxValue`);
        if (currentMin === undefined) {
          setValue(`questions.${questionIndex}.minValue`, SURVEY_RATING_DEFAULT_MIN, {
            shouldDirty: true,
          });
        }
        if (currentMax === undefined) {
          setValue(`questions.${questionIndex}.maxValue`, SURVEY_RATING_DEFAULT_MAX, {
            shouldDirty: true,
          });
        }
      }
    }, [type, answerType, questionIndex, setValue, clearErrors, getValues]);

    const showOptions = isChoiceType(type);
    const showAnswerType = type === 'text';
    const showMaxChars = type === 'text' && answerType === 'text';
    const showRange = type === 'rating' || (type === 'text' && isNumericAnswer(answerType));

    return (
      <div className="flex flex-col gap-4 rounded-xl border bg-card p-5 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2" id={questionHeadingId}>
            <NumberMark value={questionIndex + 1} />
            <span className="text-sm font-medium text-muted-foreground">Question</span>
          </div>
          <ReorderButtons
            index={questionIndex}
            total={totalQuestions}
            itemLabel="question"
            onMoveUp={onMoveUp}
            onMoveDown={onMoveDown}
            onRemove={onRemove}
          />
        </div>

        <FormField
          control={control}
          name={`questions.${questionIndex}.text`}
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel className={FIELD_LABEL_CLASS}>
                <LabelText isRequired>Question text</LabelText>
              </FormLabel>
              <FormControl>
                <Textarea
                  {...field}
                  className={TEXTAREA_CONTROL_CLASS}
                  ref={(node) => {
                    field.ref(node);
                    if (typeof ref === 'function') ref(node);
                    else if (ref) ref.current = node;
                  }}
                  rows={2}
                  placeholder="What do you want to ask?"
                  maxLength={SURVEY_QUESTION_TEXT_MAX_LENGTH}
                  aria-required="true"
                  aria-invalid={fieldState.invalid || undefined}
                />
              </FormControl>
              <div className="flex items-center justify-between gap-2">
                <FormMessage />
                <CharacterCounter
                  current={field.value?.length ?? 0}
                  max={SURVEY_QUESTION_TEXT_MAX_LENGTH}
                  className="ml-auto"
                />
              </div>
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            control={control}
            name={`questions.${questionIndex}.type`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={FIELD_LABEL_CLASS}>Type</FormLabel>
                <Select<string>
                  items={questionTypeItems}
                  value={field.value}
                  onValueChange={(next) => field.onChange(next as SurveyQuestionType)}
                >
                  <FormControl>
                    <SelectTrigger className={SELECT_TRIGGER_CLASS}>
                      <SelectValue />
                      <SelectIcon />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent className={DROPDOWN_PANEL_CLASS}>
                    {questionTypeItems.map((item) => (
                      <SelectItem
                        key={item.value}
                        value={item.value}
                        className={DROPDOWN_ITEM_CLASS}
                      >
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {showAnswerType ? (
            <FormField
              control={control}
              name={`questions.${questionIndex}.answerType`}
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel className={FIELD_LABEL_CLASS}>
                    <LabelText isRequired>Answer type</LabelText>
                  </FormLabel>
                  <Select<string>
                    items={answerTypeItems}
                    value={field.value ?? ''}
                    onValueChange={(next) => field.onChange(next as SurveyAnswerType)}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={SELECT_TRIGGER_CLASS}
                        aria-invalid={fieldState.invalid || undefined}
                      >
                        <SelectValue placeholder="Select answer type" />
                        <SelectIcon />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className={DROPDOWN_PANEL_CLASS}>
                      {answerTypeItems.map((item) => (
                        <SelectItem
                          key={item.value}
                          value={item.value}
                          className={DROPDOWN_ITEM_CLASS}
                        >
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : null}
        </div>

        {showRange ? <QuestionRangeFields questionIndex={questionIndex} /> : null}
        {showMaxChars ? <QuestionMaxCharsField questionIndex={questionIndex} /> : null}
        {showOptions ? <QuestionOptionsEditor questionIndex={questionIndex} /> : null}

        <FormField
          control={control}
          name={`questions.${questionIndex}.isRequired`}
          render={({ field }) => {
            const checkboxId = `question-${questionIndex}-required`;
            return (
              <FormItem className="flex flex-row items-center gap-2 space-y-0">
                <FormControl>
                  <Checkbox
                    id={checkboxId}
                    checked={field.value ?? false}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                  />
                </FormControl>
                <FormLabel htmlFor={checkboxId} className="cursor-pointer text-sm font-normal">
                  Mark as required
                </FormLabel>
              </FormItem>
            );
          }}
        />
      </div>
    );
  },
);

QuestionBuilder.displayName = 'QuestionBuilder';
